import { useMemo } from 'react'

import type { DateRange } from '../types'
import { getUtcHourRangeForTimeZone } from '../utils/LocalTimeHelper'
import { MAX_DATE_RANGE_DAYS } from '../utils/reportingConstants'

export interface DateContext {
  startDateUtc: string
  endDateUtc: string
}

export function useDateContext(
  dateRange: DateRange,
  timeZone?: string,
): DateContext | null {
  const { before, after } = dateRange
  const beforeMs = before?.getTime()
  const afterMs = after?.getTime()

  return useMemo(() => {
    if (!before || !after || !timeZone) {
      return null
    }

    const { startDateUtc, endDateUtc } = getUtcHourRangeForTimeZone(
      after,
      before,
      timeZone,
    )

    if (Date.parse(endDateUtc) < Date.parse(startDateUtc)) {
      return null
    }

    return { startDateUtc, endDateUtc }
  }, [beforeMs, afterMs, timeZone])
}

export function useIsDateRangeTooLarge(dateRange: DateRange): boolean {
  const { before, after } = dateRange
  const beforeMs = before?.getTime()
  const afterMs = after?.getTime()

  return useMemo(() => {
    if (!before || !after) {
      return false
    }

    const diffInMs = before.getTime() - after.getTime()
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24))

    return diffInDays > MAX_DATE_RANGE_DAYS
  }, [beforeMs, afterMs])
}
